/**
 * Meta-progression (account-level) save — separate from the per-run save.
 *
 * Persists across runs: unlocked content, lifetime stats, achievements,
 * Ascension/Heat progress, settings. Versioned + integrity-checked + fail-safe,
 * exactly like the run save. Pure (de)serialization; storage lives in renderer.
 */

import { checksum } from "./save";
import { canonicalJson } from "./state";
import type { CharacterId } from "./types";

export const CURRENT_META_VERSION = 2;
const META_MAGIC = "NULLMETA1";

export interface MetaStats {
  runsStarted: number;
  runsWon: number;
  deaths: number;
  /** Runs abandoned/quit without finishing. Added in meta schema v2. */
  abandons: number;
  totalKills: number;
  totalEliteKills: number;
  bestFloor: number;
  fastestWinTurns: number | null; // null = no win yet
  /** Wins per character. */
  winsByCharacter: Partial<Record<CharacterId, number>>;
  /** Wins per archetype tag (from winning deck's dominant tag). */
  winsByArchetype: Record<string, number>;
}

export interface MetaState {
  version: number;
  unlockedCharacters: CharacterId[];
  unlockedCards: string[]; // cardDefIds available in reward pools
  unlockedPrograms: string[];
  achievements: string[];
  /** Highest Ascension/Heat tier reached per character. */
  ascension: Partial<Record<CharacterId, number>>;
  stats: MetaStats;
  settings: MetaSettings;
}

export interface MetaSettings {
  reducedMotion: boolean;
  showDamageNumbers: boolean;
  confirmEndTurn: boolean;
}

interface MetaEnvelope {
  magic: string;
  version: number;
  checksum: string;
  meta: MetaState;
}

export function newMeta(): MetaState {
  return {
    version: CURRENT_META_VERSION,
    unlockedCharacters: ["ghost"], // breaker unlocks via a first run
    unlockedCards: [],
    unlockedPrograms: [],
    achievements: [],
    ascension: {},
    stats: {
      runsStarted: 0,
      runsWon: 0,
      deaths: 0,
      abandons: 0,
      totalKills: 0,
      totalEliteKills: 0,
      bestFloor: 0,
      fastestWinTurns: null,
      winsByCharacter: {},
      winsByArchetype: {},
    },
    settings: {
      reducedMotion: false,
      showDamageNumbers: true,
      confirmEndTurn: false,
    },
  };
}

export type MetaLoadResult =
  | { ok: true; meta: MetaState; migratedFrom?: number }
  | { ok: false; error: string };

export function serializeMeta(meta: MetaState): string {
  const body = canonicalJson(meta);
  const env: MetaEnvelope = {
    magic: META_MAGIC,
    version: meta.version,
    checksum: checksum(body),
    meta,
  };
  return JSON.stringify(env);
}

export function deserializeMeta(raw: string | null | undefined): MetaLoadResult {
  if (raw == null || raw.length === 0) return { ok: false, error: "empty" };
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return { ok: false, error: "bad-format" };
  }
  if (typeof parsed !== "object" || parsed === null) {
    return { ok: false, error: "bad-format" };
  }
  const env = parsed as Partial<MetaEnvelope>;
  if (env.magic !== META_MAGIC) return { ok: false, error: "not-nullmeta" };
  if (typeof env.version !== "number" || typeof env.meta !== "object" || env.meta === null) {
    return { ok: false, error: "bad-format" };
  }
  let body: string;
  try {
    body = canonicalJson(env.meta);
  } catch {
    return { ok: false, error: "bad-format" };
  }
  if (typeof env.checksum !== "string" || checksum(body) !== env.checksum) {
    return { ok: false, error: "checksum-mismatch" };
  }
  if (env.version > CURRENT_META_VERSION) {
    return { ok: false, error: "unsupported-version" };
  }
  let meta = env.meta as MetaState;
  let migratedFrom: number | undefined;
  if (env.version < CURRENT_META_VERSION) {
    const migrated = migrateMeta(meta, env.version);
    if (!migrated.ok) return { ok: false, error: "migration-failed" };
    meta = migrated.meta;
    migratedFrom = env.version;
  }
  if (!validateMetaShape(meta)) return { ok: false, error: "bad-format" };
  return migratedFrom != null
    ? { ok: true, meta, migratedFrom }
    : { ok: true, meta };
}

type AnyMeta = Record<string, unknown>;

/** Stepwise meta-migration ladder (N -> N+1), keyed by the version FROM. */
const META_MIGRATIONS: Record<number, (m: AnyMeta) => AnyMeta> = {
  // v1 -> v2: add the `abandons` stat (default 0). v1 saves predate it.
  1: (m) => {
    const stats = (m.stats ?? {}) as AnyMeta;
    if (typeof stats.abandons !== "number") stats.abandons = 0;
    m.stats = stats;
    return m;
  },
};

function migrateMeta(
  meta: MetaState,
  fromVersion: number,
): { ok: true; meta: MetaState } | { ok: false } {
  try {
    let v = fromVersion;
    let out = meta as unknown as AnyMeta;
    while (v < CURRENT_META_VERSION) {
      const step = META_MIGRATIONS[v];
      if (!step) return { ok: false };
      out = step(out);
      v += 1;
    }
    out.version = CURRENT_META_VERSION;
    return { ok: true, meta: out as unknown as MetaState };
  } catch {
    return { ok: false };
  }
}

/** Cheap structural validation for a loaded MetaState (defense in depth). */
export function validateMetaShape(meta: unknown): meta is MetaState {
  if (typeof meta !== "object" || meta === null) return false;
  const m = meta as Record<string, unknown>;
  if (typeof m.version !== "number") return false;
  if (!Array.isArray(m.unlockedCharacters)) return false;
  if (!Array.isArray(m.unlockedCards)) return false;
  if (!Array.isArray(m.unlockedPrograms)) return false;
  if (!Array.isArray(m.achievements)) return false;
  if (typeof m.stats !== "object" || m.stats === null) return false;
  if (typeof m.settings !== "object" || m.settings === null) return false;
  return true;
}

/**
 * Fold the result of a finished run into the meta state. Pure: returns a new
 * MetaState. Call when a run ends (victory or death).
 */
export function recordRunResult(
  meta: MetaState,
  result: {
    won: boolean;
    character: CharacterId;
    floor: number;
    turns: number;
    kills: number;
    eliteKills: number;
    archetype?: string;
  },
): MetaState {
  const next: MetaState = JSON.parse(JSON.stringify(meta));
  next.stats.runsStarted += 1; // counted on completion in this simple model
  next.stats.totalKills += result.kills;
  next.stats.totalEliteKills += result.eliteKills;
  if (result.floor > next.stats.bestFloor) next.stats.bestFloor = result.floor;
  if (result.won) {
    next.stats.runsWon += 1;
    next.stats.winsByCharacter[result.character] =
      (next.stats.winsByCharacter[result.character] ?? 0) + 1;
    if (result.archetype) {
      next.stats.winsByArchetype[result.archetype] =
        (next.stats.winsByArchetype[result.archetype] ?? 0) + 1;
    }
    if (
      next.stats.fastestWinTurns == null ||
      result.turns < next.stats.fastestWinTurns
    ) {
      next.stats.fastestWinTurns = result.turns;
    }
    // First win unlocks the breaker.
    if (!next.unlockedCharacters.includes("breaker")) {
      next.unlockedCharacters.push("breaker");
    }
  } else {
    next.stats.deaths += 1;
  }
  return next;
}

/** Record that a run was abandoned (quit before death/victory). Pure. */
export function recordRunAbandoned(meta: MetaState): MetaState {
  const next: MetaState = JSON.parse(JSON.stringify(meta));
  next.stats.abandons += 1;
  return next;
}
